import { Injectable } from '@angular/core'

/**
 * Fonte única de verdade do token da fila de atendimento (TOKEN_OTIMUS).
 *
 * O token chega pela URL do iframe montada pelo legado GeneXus:
 *   /{client}/fila-de-atendimento/identificacao/{userid}/{token}
 *
 * O token é vinculado ao cliente em que foi emitido: trocar o :client sem um
 * token novo invalida a sessão (re-autenticação obrigatória).
 *
 * Hidrata a partir da URL já no construtor para que o SocketService — que
 * conecta durante a construção do DI, antes do componente rodar — encontre o
 * token no handshake.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly STORAGE_KEY = 'fila-auth'

  private token: string = ''
  private client: string = ''

  constructor() {
    this.hydrateFromUrl()
  }

  setCredentials(client: string, token: string): void {
    if (!client || !token) {
      return
    }
    this.client = client
    this.token = token
    try {
      sessionStorage.setItem(AuthService.STORAGE_KEY, JSON.stringify({ client, token }))
    } catch {
      // sessionStorage indisponível (modo restrito) — mantém apenas em memória.
    }
  }

  getToken(): string {
    return this.token
  }

  getClient(): string {
    return this.client
  }

  hasValidTokenForClient(client: string): boolean {
    return !!this.token && !!client && this.client === client
  }

  clear(): void {
    this.token = ''
    this.client = ''
    try {
      sessionStorage.removeItem(AuthService.STORAGE_KEY)
    } catch {
      // ignore
    }
  }

  /**
   * Extrai {client, token} de window.location quando a URL é a rota de
   * identificação da fila. Caso não haja token na URL (navegação interna),
   * recai no que estiver persistido em sessionStorage.
   */
  hydrateFromUrl(): void {
    const segments = window.location.pathname.split('/').filter((s) => s.length > 0)
    // ['{client}', 'fila-de-atendimento', 'identificacao', '{userid}', '{token}']
    const isFilaIdent =
      segments[1] === 'fila-de-atendimento' && segments[2] === 'identificacao' && !!segments[4]

    if (isFilaIdent) {
      const client = segments[0]
      const token = this.safeDecode(segments[4])
      this.setCredentials(client, token)
      return
    }

    if (!this.token) {
      this.restoreFromStorage()
    }
  }

  private restoreFromStorage(): void {
    try {
      const raw = sessionStorage.getItem(AuthService.STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        this.client = parsed.client || ''
        this.token = parsed.token || ''
      }
    } catch {
      // ignore
    }
  }

  private safeDecode(value: string): string {
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }
}
